import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { leaveRequestApi } from "../../../api/leaveRequestApi";
import { useNavigate } from "react-router-dom";
import { fetchLeaveMaster } from "../../../api/leaveMasterApi";

const todayISO = (() => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t.toISOString().split("T")[0];
})();

const calculateDays = (from, to, leaveType, govtHolidays = []) => {
  if (!from || !to) return 0;
  if (leaveType === "halfday") return 1;

  const start = new Date(from);
  const end = new Date(to);
  let count = 0;

  const holidaySet = new Set(
    govtHolidays.map(h => h.dateOnly)
  );

  let current = new Date(start);

  while (current <= end) {
    const iso = current.toISOString().split("T")[0];
    const day = current.getDay();

    // Exclude Sunday & Govt Holiday
    if (day !== 0 && !holidaySet.has(iso)) {
      count++;
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
};

// Count weekends in range
const countWeekends = (from, to) => {
  if (!from || !to) return { saturdays: 0, sundays: 0 };
  const start = new Date(from);
  const end = new Date(to);
  let saturdays = 0;
  let sundays = 0;
  let current = new Date(start);

  while (current <= end) {
    const day = current.getDay();
    if (day === 6) saturdays++;
    else if (day === 0) {
      if (current > start && current < end) sundays++;
    }
    current.setDate(current.getDate() + 1);
  }

  return { saturdays, sundays };
};

const isSunday = (isoDate) => isoDate && new Date(isoDate).getDay() === 0;

const getDayName = (isoDate) => {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleDateString("en-IN", {
    weekday: "long",
  });
};

const LeaveRequestForm = ({
  onSuccess,
  onCancel,
  selectedEmployee = null,
  isAdmin = false,
}) => {

  const { hrmsUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const employee_id = isAdmin
    ? selectedEmployee?.vchEmpCode
    : hrmsUser?.employee_id;

  const [formData, setFormData] = useState({
    leave_from: "",
    leave_to: "",
    leaveType: "",
    reason: "",
    applying_to: "",
    managerId: "",
    attachment: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [minLeaveTo, setMinLeaveTo] = useState("");
  const [weekendInfo, setWeekendInfo] = useState({ saturdays: 0, sundays: 0 });
  const [leaveMaster, setLeaveMaster] = useState([]);
  const [loadingLeaveMaster, setLoadingLeaveMaster] = useState(true);
  const [govtHolidays, setGovtHolidays] = useState([]);
  const [hotelManagers, setHotelManagers] = useState([]);

  const daysCount = useMemo(
    () =>
      calculateDays(
        formData.leave_from,
        formData.leave_to,
        formData.leaveType,
        govtHolidays
      ),
    [formData.leave_from, formData.leave_to, formData.leaveType, govtHolidays]
  );

  useEffect(() => {
    const fetchGovtHolidays = async () => {
      try {
        const data = await leaveRequestApi.getGovtHolidays();

        // normalize API ISO dates → YYYY-MM-DD
        const normalized = data.map(h => ({
          HolidayName: h.HolidayName,
          HolidayType: h.HolidayType,
          dateOnly: h.HolidayDate.split("T")[0]
        }));

        setGovtHolidays(normalized);
      } catch (error) {
        console.error("Failed to load govt holidays", error);
      }
    };

    fetchGovtHolidays();
  }, []);

  useEffect(() => {
    if (!formData.leave_from || !formData.leave_to) {
      setWeekendInfo({ saturdays: 0, sundays: 0 });
    } else {
      setWeekendInfo(countWeekends(formData.leave_from, formData.leave_to));
    }

    if (formData.leave_from) {
      setMinLeaveTo(formData.leave_from);
      if (formData.leave_to && new Date(formData.leave_to) < new Date(formData.leave_from)) {
        setFormData((prev) => ({ ...prev, leave_to: "" }));
      }
    } else {
      setMinLeaveTo("");
    }

  }, [formData.leave_from, formData.leave_to]);

  useEffect(() => {
    if (formData.leaveType === "halfday" && formData.leave_from) {
      setFormData(prev => ({
        ...prev,
        leave_to: formData.leave_from
      }));
    }
  }, [formData.leaveType, formData.leave_from]);

  useEffect(() => {
    const loadLeaveMaster = async () => {
      try {
        const data = await fetchLeaveMaster();
        console.log("Leave master fetched:", data);
        setLeaveMaster(data);

      } catch (error) {
        console.error("Failed to load leave master", error);
      } finally {
        setLoadingLeaveMaster(false);
      }
    };

    loadLeaveMaster();
  }, []);

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const employees = await leaveRequestApi.getAllEmployees();
        const managers = (Array.isArray(employees) ? employees : []).filter(
          (emp) => emp?.vchDesignation?.trim()?.toLowerCase().includes("manager")
        );
        setHotelManagers(managers);
      } catch (error) {
        console.error("Failed to load managers", error);
      }
    };

    loadManagers();
  }, []);

  const isGovtHoliday = (isoDate) => {
    return govtHolidays.some(h => h.dateOnly === isoDate);
  };

  const getHolidayName = (isoDate) => {
    const holiday = govtHolidays.find(h => h.dateOnly === isoDate);
    return holiday ? holiday.HolidayName : "";
  };

  const countHolidaysInRange = () => {
    if (!formData.leave_from || !formData.leave_to) return 0;

    let count = 0;
    let current = new Date(formData.leave_from);
    const end = new Date(formData.leave_to);

    while (current <= end) {
      const iso = current.toISOString().split("T")[0];
      if (isGovtHoliday(iso)) count++;
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    setError("");

    // Handle date fields
    if (name === "leave_from" || name === "leave_to") {
      const selectedDate = new Date(value);
      const day = selectedDate.getDay();

      // Prevent selecting Sunday
      if (day === 0) {
        setError("The selected date falls on [Sunday]. Please choose a different date.");
        setFormData({ ...formData, [name]: "" });
        return;
      }

      // Prevent selecting Government Holiday
      if (isGovtHoliday(value)) {
        setError(
         `The selected date is a government holiday : [${getHolidayName(value)}]. Please select another date.`
        );
        setFormData({ ...formData, [name]: "" });
        return;
      }
    }

    // Handle attachment
    if (name === "attachment") {
      const file = files[0];

      if (file) {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        if (!allowedTypes.includes(file.type)) {
          setError("Only PDF and Word files are allowed.");
          return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          setError("File size should not exceed 5 MB.");
          return;
        }

        setFormData({ ...formData, attachment: file });
      }

      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isAdmin && !selectedEmployee) {
      setError("Employee not selected for leave application.");
      return;
    }

    try {
      /* BASIC VALIDATIONS */

      if (!formData.leave_from || !formData.leave_to) {
        throw new Error("Please select both dates.");
      }

      if (new Date(formData.leave_from) > new Date(formData.leave_to)) {
        throw new Error("Leave From cannot be after Leave To.");
      }

      if (new Date(formData.leave_from) < new Date(todayISO)) {
        throw new Error("Leave From cannot be in the past.");
      }

      if (formData.leaveType === "halfday") {
        if (formData.leave_from !== formData.leave_to) {
          throw new Error("Half Day leave can be applied for only one day.");
        }
      }

      if (!formData.applying_to) {
        throw new Error("Please select authority (MD, GM or Hotel Manager).");
      }

      if (formData.applying_to === "managerId" && !formData.managerId) {
        throw new Error("Please select hotel manager name.");
      }

      /* quota validation */

      const annualQuota = getSelectedLeaveQuota();

      if (daysCount > annualQuota) {
        throw new Error(
          `You can apply only ${annualQuota} days for this leave type`
        );
      }

      /* submit to API */

      setLoading(true);

      const payload = new FormData();
      const applyingToValue =
        formData.applying_to === "managerId"
          ? "MANAGER"
          : formData.applying_to;

      Object.entries({ ...formData, applying_to: applyingToValue }).forEach(([key, value]) => {
        if (typeof value === "string") {
          payload.append(key, value.trim());
        } else {
          payload.append(key, value);
        }
      });

      if (formData.applying_to === "managerId" && formData.managerId) {
        payload.append("manager_employee_id", formData.managerId);
      }

      payload.append("employee_id", employee_id);

      const response = await leaveRequestApi.createLeaveRequest(payload);

      if (response?.success) {
        if (
          Array.isArray(response.data) &&
          response.data[0]?.Message?.includes("Error")
        ) {
          throw new Error(response.data[0].Message);
        }

        window.alert("Leave request submitted successfully.");

        if (isAdmin) {
          navigate("/employees-leave");
          return;
        }

        onSuccess && onSuccess(response.data);
      } else {
        throw new Error(response?.message || "Failed to create leave request");
      }
    } catch (err) {
      setError(err.message || "Failed to create leave request");
    } finally {
      setLoading(false);
    }
  };

  const inputClassForDate = (isoDate) => {
    if (!isoDate) return "form-control ps-5";

    const day = new Date(isoDate).getDay();

    if (isGovtHoliday(isoDate)) {
      return "form-control ps-5 border-info bg-info bg-opacity-10";
    }

    if (day === 6) {
      return "form-control ps-5 border-warning";
    }

    if (day === 0) {
      return "form-control ps-5 border-danger";
    }

    return "form-control ps-5";
  };

  const getSelectedLeaveQuota = () => {
    const selectedLeave = leaveMaster.find(
      (l) => l.leave_type === formData.leaveType
    );
    return selectedLeave ? selectedLeave.balance : 0;
  };

  return (
    <div className="card shadow-sm leave-surface-card leave-form-card">
      <div className="card-header leave-surface-header">
        <h5 className="card-title mb-0">Apply Leave</h5>
      </div>
      <div className="card-body leave-surface-body">
        <form onSubmit={handleSubmit} className="leave-form">
          {error && !error.toLowerCase().includes("file") && (
            <div className="alert alert-danger">{error}</div>
          )}

          {isAdmin && selectedEmployee && (
            <div className="col-12 mb-3">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  value={selectedEmployee.vchFullName}
                  readOnly
                />
                <label>Employee Name</label>
              </div>
            </div>
          )}

          <div className="row g-4">

            {/* Applying To */}
            <div className="col-12">
              <label className="form-label">
                Applying To <span className="text-danger">*</span>
              </label>
              <div className="d-flex flex-wrap gap-3 mt-2">
                {/* MD */}
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="apply-md"
                    name="applying_to"
                    value="MD"
                    checked={formData.applying_to === "MD"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applying_to: e.target.value,
                        managerId: "",
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="apply-md">
                    Managing Director (MD)
                  </label>
                </div>

                {/* GM */}
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="apply-gm"
                    name="applying_to"
                    value="GM"
                    checked={formData.applying_to === "GM"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applying_to: e.target.value,
                        managerId: "",
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="apply-gm">
                    General Manager (GM)
                  </label>
                </div>

                {/* Hotel Manager */}
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="apply-hotel-manager"
                    name="applying_to"
                    value="managerId"
                    checked={formData.applying_to === "managerId"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applying_to: e.target.value,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="apply-hotel-manager">
                    Hotel Manager
                  </label>
                </div>
              </div>

              {formData.applying_to === "managerId" && (
                <div className="mt-3 col-12 col-lg-4">
                  <select
                    className="form-select"
                    name="managerId"
                    value={formData.managerId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Manager</option>
                    {hotelManagers.length === 0 && (
                      <option value="" disabled>
                        No managers found
                      </option>
                    )}
                    {hotelManagers.map((manager) => (
                      <option
                        key={manager.Id || manager.id || manager.vchEmpCode}
                        value={manager.vchEmpCode || ""}
                      >
                        {manager.vchFullName || "-"}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="border rounded p-3 bg-light mt-3 leave-help-panel">
                <div className="d-flex">
                  <i className="bi bi-info-circle text-primary me-2"></i>
                  <div>
                    <strong>Leave Application Advisory:</strong>
                    <ul className="mb-0 ps-3 small text-muted">
                      <li>Hotel staff must apply to their respective Hotel Manager.</li>
                      <li>Hotel Managers and other officials shall apply to MD / GM only.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave From */}
            <div className="col-md-6">
              <div className="position-relative">
                <i className="bi bi-calendar-event position-absolute top-50 translate-middle-y ms-2 text-primary" />
                <div className="form-floating">
                  <input
                    type="date"
                    className={inputClassForDate(formData.leave_from)}
                    id="leave_from"
                    name="leave_from"
                    value={formData.leave_from}
                    onChange={handleInputChange}
                    min={todayISO}
                    required
                  />
                  <label htmlFor="leave_from">
                    Leave From <span className="text-danger">*</span>
                  </label>
                </div>

                {/* Context info */}
                {formData.leave_from && (
                  <div className="mt-1">
                    {!isSunday(formData.leave_from) && (
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        {getDayName(formData.leave_from)}
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Leave To */}
            <div className="col-md-6">
              <div className="position-relative">
                <i className="bi bi-calendar2-range position-absolute top-50 translate-middle-y ms-2 text-primary" />
                <div className="form-floating">
                  <input
                    type="date"
                    className={inputClassForDate(formData.leave_to)}
                    id="leave_to"
                    name="leave_to"
                    value={formData.leave_to}
                    onChange={handleInputChange}
                    min={minLeaveTo || todayISO}
                    required
                  />
                  <label htmlFor="leave_to">
                    Leave To <span className="text-danger">*</span>
                  </label>
                </div>

                {formData.leave_to && (
                  <div className="mt-1">
                    {!isSunday(formData.leave_to) && (
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        {getDayName(formData.leave_to)}
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Summary Box */}
            <div className="col-12">
              <div className="bg-light border rounded p-3 shadow-sm leave-summary-card">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

                  {/* Left: Policy + Total Days */}
                  <div className="d-flex align-items-start">
                    <i className="bi bi-shield-check fs-5 me-2 text-primary"></i>
                    <div>
                      <div className="fw-semibold">
                        Total Applicable Leave Days: {daysCount}
                      </div>
                      <small className="text-muted d-block">
                        Sundays and government holidays within the selected range are
                        <strong> excluded</strong> from leave calculation.
                      </small>
                    </div>
                  </div>

                  {/* Right: Informational Badges */}
                  <div className="d-flex flex-wrap align-items-center gap-2">

                    {(countHolidaysInRange() > 0 || weekendInfo.sundays > 0) && (
                      <span className="badge bg-secondary-subtle text-secondary">
                        {[
                          countHolidaysInRange() > 0 &&
                          `${countHolidaysInRange()} Holiday${countHolidaysInRange() > 1 ? "s" : ""}`,
                          weekendInfo.sundays > 0 &&
                          `${weekendInfo.sundays} Sunday${weekendInfo.sundays > 1 ? "s" : ""}`
                        ]
                          .filter(Boolean)
                          .join(" and ")}{" "}
                        in selected range
                      </span>
                    )}

                  </div>
                </div>
              </div>
            </div>

            {/* Leave Type */}
            <div className="col-md-6">
              <div className="position-relative">
                <i className="bi bi-list-task position-absolute top-50 translate-middle-y ms-2 text-primary" />
                <div className="form-floating">
                  <select
                    className="form-select"
                    name="leaveType"
                    value={formData.leaveType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Leave Type</option>
                    {leaveMaster.map((leave) => (
                      <option
                        key={leave.leave_type}
                        value={leave.leave_type}
                      >
                        {leave.leave_type}
                      </option>
                    ))}
                  </select>

                  <label htmlFor="leave_type">
                    Leave Type <span className="text-danger">*</span>
                  </label>
                </div>

                {/* show balance info */}
                {formData.leaveType && (
                  <small className="d-block mt-1 text-muted">
                    Annual Quota:{" "}
                    <span className="fw-semibold">
                      {getSelectedLeaveQuota()} days
                    </span>
                  </small>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="col-md-6">
              <div className="position-relative">
                <i className="bi bi-pencil-square position-absolute top-0 ms-2 mt-4 text-primary" />
                <div className="form-floating">
                  <textarea
                    className="form-control ps-5"
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/(\r\n|\n|\r)/gm, " ");
                      if (value.length <= 60) {
                        setFormData({ ...formData, reason: value });
                      }
                    }}
                    placeholder="Short reason only (e.g., Medical emergency)"
                    style={{ minHeight: "100px", maxHeight: "120px" }}
                    maxLength={60}
                    required
                  >
                  </textarea>
                  <label htmlFor="reason">
                    Reason (Short & Specific) <span className="text-danger">*</span>
                  </label>
                </div>

                <small className="text-muted">
                  {formData.reason.length}/60 characters
                </small>
              </div>
            </div>

            {/* Attachment Upload */}
            <div className="col-md-12">
              <div className="position-relative">
                <i className="bi bi-paperclip position-absolute top-50 translate-middle-y ms-2 text-primary" />
                <div className="form-floating">
                  <input
                    type="file"
                    className="form-control ps-5"
                    id="attachment"
                    name="attachment"
                    onChange={handleInputChange}
                    accept=".pdf,.doc,.docx"
                  />
                  <label htmlFor="attachment">Attachment (PDF/Word)</label>
                </div>

                {/* Show validation error only for file issues */}
                {error && error.toLowerCase().includes("file") && (
                  <small className="text-danger d-block mt-1">{error}</small>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="col-12 d-flex flex-column flex-sm-row gap-2 justify-content-end mt-2 leave-form-actions">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || daysCount === 0}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Creating...
                  </>
                ) : (
                  "Create Request"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveRequestForm;
